import React from "react";
import { string, bool, node, elementType, number } from "prop-types";
import {
	utilityClassName,
	utilityProps,
	utilityPropTypes,
} from "@insightfulscience/atomic-react/props/utility";
import pureProps, { deepMergeProps } from "@insightfulscience/atomic-react/utils/pureProps";
import { Col, Container, Row } from "../../../Grid";
import Heading from "../../../Heading";
import Paragraph from "../../../Paragraph";
import Line from "../../../Line";
import Section from "../../../Section";
import { classes } from "../../../utils/classes";
import { colorPropTypes, sxClassName } from "../../../props/sx";
import { spacingClassName, spacingProps, spacingPropTypes } from "../../../props/spacing";

const PricingSectionHeader = ({ title, children }) => {
	return (
		<Row utility={{ justify: "center" }}>
			<Col lg={9}>
				<Heading Tag="h2" sx={{ fontSize: "xl", textAlign: "center" }} spacing={{ mb: 6 }}>
					{title}
				</Heading>
				{children && (
					<Paragraph sx={{ fontSize: "l", textAlign: "center" }} spacing={{ mb: 6 }}>
						{children}
					</Paragraph>
				)}
			</Col>
		</Row>
	);
};

PricingSectionHeader.propTypes = {
	title: string.isRequired,
	children: node,
};

const PricingSectionBody = ({ lgCellWidth = 9, children }) => {
	return (
		<Row mx px utility={{ justify: "center" }}>
			<Col lg={lgCellWidth}>
				<Row mx utility={{ justify: "center" }}>
					{children}
				</Row>
			</Col>
		</Row>
	);
};

PricingSectionBody.propTypes = {
	lgCellWidth: Col.propTypes.lg,
	children: node.isRequired,
};

const PricingSectionBodyItem = ({ center = false, children, ...props }) => {
	return (
		<Col md={center ? 6 : "auto"} {...props}>
			{children}
		</Col>
	);
};

PricingSectionBodyItem.propTypes = {
	center: bool,
	children: node.isRequired,
};

PricingSectionBody.Item = PricingSectionBodyItem;

const PricingSectionFooter = ({ lgCellWidth = 9, children }) => {
	return (
		<Row mx px utility={{ justify: "center" }}>
			<Col lg={lgCellWidth}>{children}</Col>
		</Row>
	);
};

PricingSectionFooter.propTypes = {
	lgCellWidth: number,
	children: node.isRequired,
};

const PricingSectionFooterTitle = ({ children }) => {
	return (
		<Heading Tag="h3" sx={{ fontSize: "m" }} spacing={{ mb: 2 }}>
			{children}
		</Heading>
	);
};

PricingSectionFooterTitle.propTypes = {
	children: string,
};

const PricingSectionFooterList = ({ isListPros = true, children }) => {
	return (
		<ul
			className={classes(
				"ul",
				isListPros ? "ul-check" : "ul-no-check",
				...sxClassName({ fontSize: "m" }),
				...spacingClassName({ mt: 4, mb: 8 }),
			)}
		>
			{children}
		</ul>
	);
};

PricingSectionFooterList.propTypes = {
	isListPros: bool,
	children: node.isRequired,
};

const PricingSectionFooterListItem = ({ children }) => {
	return <li className="li ul__li text-m">{children}</li>;
};

PricingSectionFooterListItem.propTypes = {
	children: string.isRequired,
};

const PricingSectionFooterDescription = ({ children, ...props }) => {
	return (
		<Paragraph sx={{ fontSize: "s", fontColor: "black--46" }} spacing={{ mt: 0 }} {...props}>
			{children}
		</Paragraph>
	);
};

PricingSectionFooterDescription.propTypes = {
	children: node.isRequired,
	...Paragraph.propTypes,
};

PricingSectionFooter.Title = PricingSectionFooterTitle;
PricingSectionFooter.List = PricingSectionFooterList;
PricingSectionFooter.List.Item = PricingSectionFooterListItem;
PricingSectionFooter.Description = PricingSectionFooterDescription;

const PricingSection = ({
	Tag = Section,
	children,
	isSeparated = true,
	className = "",
	bgColor,
	utility = { ...utilityProps },
	spacing = { ...spacingProps },
}) => {
	return (
		<Tag
			className={classes(
				"section",
				"pb-0",
				className,
				...utilityClassName(deepMergeProps({ bgColor }, pureProps(utility))),
				...spacingClassName(spacing),
			)}
		>
			<Container>
				{children}
				{isSeparated && (
					<Row mx px>
						<Col>
							<Line spacing={{ mt: 10 }} />
						</Col>
					</Row>
				)}
			</Container>
		</Tag>
	);
};

PricingSection.propTypes = {
	Tag: elementType,
	children: node.isRequired,
	isSeparated: bool,
	bgColor: colorPropTypes,
	utility: utilityPropTypes,
	spacing: spacingPropTypes,
	className: string,
};

PricingSection.Header = PricingSectionHeader;
PricingSection.Body = PricingSectionBody;
PricingSection.Footer = PricingSectionFooter;

export default PricingSection;
